import { useCallback, useRef, useEffect, useState } from "react";

import {
  AddressCompleteSearchProvider,
  AddressFieldWithoutSearchConfig,
  CountryFieldConfig,
  FreeFormAddressFieldConfig,
} from "@evergiving/common/dist/es2015/form";
import { useSelector } from "react-redux";

import { MelissaResponse } from "@/lib/misc/MelissaResponse";
import { LoqateResponse } from "@/lib/misc/LoqateResponse";

import { RootState } from "../redux/types";

interface UseAddressFieldWithSearchProps {
  searchUrl: URL;
  searchProvider: AddressCompleteSearchProvider;
  countryFieldConfig: CountryFieldConfig | undefined;
  addressFieldWithoutSearch: AddressFieldWithoutSearchConfig;
}

interface UseAddressFieldWithSearchReturn {
  handleSearch: () => Promise<void>;
  handleClear: () => void;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  countryName: string | undefined;
  freeFormAddressFieldConfig: FreeFormAddressFieldConfig;
  results: MelissaResponse | LoqateResponse | null;
  loading: boolean;
  text: string;
}

export const useAddressFieldWithSearch = ({
  searchUrl,
  searchProvider,
  countryFieldConfig,
  addressFieldWithoutSearch,
}: UseAddressFieldWithSearchProps): UseAddressFieldWithSearchReturn => {
  const [results, setResults] = useState<MelissaResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [text, setText] = useState<string>("");

  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const countryName = currentCountryCode
    ? countryFieldConfig?.supportedCountryNameTexts[currentCountryCode]
    : undefined;

  const freeFormAddressFieldConfig =
    (currentCountryCode
      ? addressFieldWithoutSearch.freeForm.byCountry[currentCountryCode]
      : undefined) ?? addressFieldWithoutSearch.freeForm.defaultConfig;

  const loadingCount = useRef<number>(0);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controller = useRef<AbortController | null>(null);

  const search = useCallback(
    async (searchText: string) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      if (controller.current) {
        controller.current.abort();
        controller.current = null;
      }

      const query = searchText.trim();

      if (query.length > 0 && currentCountryCode) {
        const url = searchUrl;

        url.search = new URLSearchParams({
          query,
          maxNumRecords: String(10),
          countryCode: currentCountryCode,
          searchProvider,
        }).toString();

        try {
          loadingCount.current += 1;
          setLoading(true);
          controller.current = new AbortController();
          const { signal } = controller.current;
          const result = await fetch(url.toString(), {
            signal,
          });

          const body = await result.json();

          setResults(body);
        } catch (e) {
          if (!(e instanceof DOMException) || e.name !== "AbortError") {
            console.warn(e);
          }
        } finally {
          loadingCount.current -= 1;

          if (loadingCount.current === 0) {
            setLoading(false);
          }
        }
      }
    },
    [currentCountryCode, searchProvider, searchUrl]
  );

  const handleSearch = useCallback(async () => {
    await search(text);
  }, [text, search]);

  const handleClear = useCallback(() => {
    if (controller.current) {
      controller.current.abort();
      controller.current = null;
    }
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    setText("");
    setResults(null);
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target) {
        const newValue = event.target.value;
        if (newValue !== text) {
          setText(newValue);
          if (newValue.trim() !== text.trim()) {
            if (controller.current) {
              controller.current.abort();
              controller.current = null;
            }

            if (timeout.current) {
              clearTimeout(timeout.current);
            }

            timeout.current = setTimeout(() => search(newValue), 500);
          }
        }
      }
    },
    [text, search]
  );

  useEffect(() => {
    if (loadingCount.current === 0) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, []);

  return {
    handleSearch,
    handleClear,
    handleChange,
    countryName,
    freeFormAddressFieldConfig,
    results,
    loading,
    text,
  };
};
